import { Box, Typography, useTheme } from "@mui/material";
import { Controller, type UseFormReturn } from "react-hook-form";
import { useGoalsRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { ReminderCreateFormModel } from "web_myplann/Reminders/Forms/reminderFormSchema";
import { useReminderRecurrenceOptions } from "web_myplann/Reminders/Hooks/useReminderRecurrenceOptions";
import type { Reminder } from "web_myplann/Reminders/Types/ReminderTypes";
import { getReminderMinDate } from "web_myplann/Reminders/Utils/reminderMinDate";
import { FlatRowDateDayPanel } from "web_myplann/Shared/Components/FlatRow/FlatRowDateDayPanel";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { FlatRowGroup } from "web_myplann/Shared/Components/FlatRow/FlatRowGroup";
import { ReminderDateCalendar } from "web_myplann/Shared/Components/ReminderDateCalendar";
import { ReminderDuplicateAlert } from "web_myplann/Shared/Components/ReminderDuplicateAlert";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";

type Props = {
	form: UseFormReturn<ReminderCreateFormModel>;
	duplicate: Reminder | null;
	onOpenDuplicate: (reminder: Reminder) => void;
	duplicateAlertId: string;
};

export const ReminderCreateFields = ({
	form,
	duplicate,
	onOpenDuplicate,
	duplicateAlertId,
}: Props) => {
	const { t } = useResource();
	const theme = useTheme();
	const hasGoalsRight = useGoalsRightOptional();
	const { control } = form;

	const minDate = getReminderMinDate();
	const recurrenceOptions = useReminderRecurrenceOptions();

	return (
		<>
			{hasGoalsRight && (
				<FlatRowGroup>
					<FlatRowFormInput
						control={control}
						name="name"
						label={t(Resources.Reminders.Form.nameLabel)}
						placeholder={t(Resources.Reminders.Form.defaultName)}
						isLast
					/>
				</FlatRowGroup>
			)}
			<Box
				mt={hasGoalsRight ? theme.spacingTokens.md : undefined}
				bgcolor={theme.palette.background.paper}
				borderTop={theme.colors.border.solid(theme.palette.surface.border)}
				borderBottom={theme.colors.border.solid(theme.palette.surface.border)}
			>
				<Controller
					control={control}
					name="date"
					render={({ field: { value, onChange, onBlur } }) => (
						<>
							{hasGoalsRight && (
								<ReminderDateCalendar
									value={value instanceof Date ? value : null}
									minDate={minDate}
									onSelect={(date) => {
										onChange(date);
										onBlur();
									}}
								>
									{duplicate && (
										<ReminderDuplicateAlert
											id={duplicateAlertId}
											duplicate={duplicate}
											onOpen={onOpenDuplicate}
										/>
									)}
								</ReminderDateCalendar>
							)}
							{!hasGoalsRight && (
								<Box
									px={theme.spacingTokens.lg}
									pt={theme.spacingTokens.md}
									pb={theme.spacingTokens.sm}
								>
									<Typography
										variant="caption"
										component="div"
										color={theme.palette.text.secondary}
										mb={theme.spacingTokens.sm}
									>
										{value
											? t(Resources.Reminders.Form.selectedDate, {
													date: dateFormatter(value),
												})
											: t(Resources.Reminders.Form.selectDate)}
									</Typography>

									{duplicate && (
										<ReminderDuplicateAlert
											id={duplicateAlertId}
											duplicate={duplicate}
											onOpen={onOpenDuplicate}
										/>
									)}

									<FlatRowDateDayPanel
										value={value instanceof Date ? value : null}
										minDate={minDate}
										onSelect={(date) => {
											onChange(date);
											onBlur();
										}}
									/>
								</Box>
							)}
						</>
					)}
				/>

				<FlatRowFormSelect
					control={control}
					name="recurrence"
					label={t(Resources.Reminders.Form.recurrenceLabel)}
					options={recurrenceOptions}
					drawerTitle={t(Resources.Reminders.Form.recurrenceLabel)}
					isLast
				/>
			</Box>
		</>
	);
};
